import React, {useMemo} from 'react';
import {VehicleType} from '@/Pages/VehicleTypes/store/types';
import ErrorSection from '../ErrorSection/ErrorSection';
import {ErrorItem} from '../ErrorSection/ErrorSection.types';
import {useInvalidCostFactorHelpers} from '../useInvalidCostFactorHelpers';
import {
  isVehicleTypeErrorCode,
  getVehicleTypeError,
  getVehicleTypeLink,
  VehicleTypeErrorCode
} from '../InvalidCostFactorsPopup.utils';
import {VehicleTypeErrorsSectionProps} from './VehicleTypeErrorsSection.types';

const VehicleTypeErrorsSection: React.FC<VehicleTypeErrorsSectionProps> = ({
  invalidCostFactors,
  busValues,
  busPartners,
  countries,
  costYear,
  vehicleTypeCostEntries
}) => {
  const {getVehicleContext, logMissing} = useInvalidCostFactorHelpers(
    busValues,
    busPartners,
    countries
  );

  const vehicleTypeErrors = useMemo(() => {
    if (!costYear) return [];

    const errors = (invalidCostFactors ?? []).filter(invalidCostFactor =>
      isVehicleTypeErrorCode(invalidCostFactor.errorCode)
    );
    const vehicleTypeToErrorCodes = new Map<string, VehicleTypeErrorCode[]>();

    const groupedVehicleTypeErrors: Map<
      string,
      {vehicleType: VehicleType; errors: ErrorItem[]}
    > = new Map();

    for (const error of errors) {
      const context = getVehicleContext(error);
      if (!context) continue;

      const {vehicleSettings, busPartner} = context;

      // Find vehicle type cost entry
      const vehicleTypeCostEntriesId =
        busPartner.id + busPartner.countryId + String(costYear);
      const entries = vehicleTypeCostEntries[vehicleTypeCostEntriesId];

      if (!entries) {
        logMissing('vehicle type cost entries', error);
        continue;
      }

      const vehicleTypeCostEntry = vehicleSettings.busPartnerBusTypeCostEntryId
        ? entries.busPartnerBusTypeCostEntries.find(
            ({id}: any) => id === vehicleSettings.busPartnerBusTypeCostEntryId
          )
        : entries.countryBusTypeCostEntries.find(
            ({id}: any) => id === vehicleSettings.countryBusTypeCostEntryId
          );

      if (!vehicleTypeCostEntry) {
        logMissing('vehicle type cost entry', error);
        continue;
      }

      // Avoid duplicates
      const prevErrorCodes =
        vehicleTypeToErrorCodes.get(vehicleTypeCostEntry.id) ?? [];
      const currErrorCode = error.errorCode as VehicleTypeErrorCode;
      if (prevErrorCodes.includes(currErrorCode)) continue;
      vehicleTypeToErrorCodes.set(vehicleTypeCostEntry.id, [
        ...prevErrorCodes,
        currErrorCode
      ]);

      const errorMessage = getVehicleTypeError(currErrorCode);

      const group = groupedVehicleTypeErrors.get(vehicleTypeCostEntry.id) ?? {
        vehicleType: vehicleTypeCostEntry.busType,
        errors: [] as ErrorItem[]
      };
      group.errors.push({code: currErrorCode, message: errorMessage});
      groupedVehicleTypeErrors.set(vehicleTypeCostEntry.id, group);
    }
    return Array.from(groupedVehicleTypeErrors.values());
  }, [
    costYear,
    invalidCostFactors,
    getVehicleContext,
    logMissing,
    vehicleTypeCostEntries
  ]);

  if (vehicleTypeErrors.length === 0) return null;

  return (
    <>
      {vehicleTypeErrors.map(({vehicleType, errors: errorList}) => (
        <ErrorSection
          key={`vehicle-type-${vehicleType.id}`}
          id={`vehicle-type-${vehicleType.id}`}
          title={
            <>
              The following vehicle type details for{' '}
              {getVehicleTypeLink(vehicleType.id, vehicleType.name)} are
              missing:
            </>
          }
          errors={errorList}
        />
      ))}
    </>
  );
};

export default React.memo(VehicleTypeErrorsSection);
